import { PaperPlaneIcon } from '@radix-ui/react-icons';
import { Smile } from 'lucide-react';
import { Popover } from 'radix-ui';
import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';

import { useTranslation } from '~/app';
import { Button } from '~/components/ui/button';
import {
  EmojiPicker,
  EmojiPickerContent,
  EmojiPickerFooter,
  EmojiPickerSearch,
} from '~/components/ui/emoji-picker';
import { Textarea } from '~/components/ui/textarea';
import { cn } from '~/utils/styles';

import { addRecentEmoji, readRecentEmojis } from './chatRecentEmojis';

const MAX_LINES = 2;

const FRIMOUSSE_LOCALES = new Set(['en', 'de', 'es']);

function toFrimousseLocale(locale: string): 'en' | 'de' | 'es' {
  const base = locale.split('-')[0]?.toLowerCase() ?? 'en';
  if (FRIMOUSSE_LOCALES.has(base)) return base as 'en' | 'de' | 'es';
  return 'en';
}

export interface ChatComposerProps {
  canSend: boolean;
  onSend: (message: string) => void;
  placeholder: string;
  sendLabel: string;
}

const ChatComposer: React.FC<ChatComposerProps> = ({ canSend, onSend, placeholder, sendLabel }) => {
  const { t, locale } = useTranslation();
  const [draft, setDraft] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const isComposingRef = useRef(false);
  const maxHeightCapRef = useRef<number | null>(null);
  const oneLineHeightRef = useRef<number | null>(null);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [recents, setRecents] = useState<string[]>([]);

  const trimmed = draft.trim();
  const disabled = !canSend || !trimmed;

  const handleEmojiOpenChange = useCallback((open: boolean) => {
    setEmojiOpen(open);
    if (open) setRecents(readRecentEmojis());
  }, []);

  const applyEmoji = useCallback(
    (emoji: string) => {
      if (!emoji || !canSend) return;
      addRecentEmoji(emoji);
      setRecents(readRecentEmojis());
      setDraft((prev) => {
        const el = textareaRef.current;
        const start = el ? el.selectionStart : prev.length;
        const end = el ? el.selectionEnd : prev.length;
        const safeStart = Math.min(Math.max(0, start), prev.length);
        const safeEnd = Math.min(Math.max(0, end), prev.length);
        const next = prev.slice(0, safeStart) + emoji + prev.slice(safeEnd);
        const pos = safeStart + emoji.length;
        queueMicrotask(() => {
          const ta = textareaRef.current;
          if (!ta) return;
          ta.focus();
          ta.setSelectionRange(pos, pos);
        });
        return next;
      });
      setEmojiOpen(false);
    },
    [canSend],
  );

  const recalcMaxHeightCap = useCallback(() => {
    const el = textareaRef.current;
    if (!el || isComposingRef.current) return;
    const saved = el.value;
    const ss = el.selectionStart;
    const se = el.selectionEnd;
    el.value = 'M';
    el.style.height = 'auto';
    const hOneLine = el.scrollHeight;
    el.value = 'M\nM';
    const hTwoLines = el.scrollHeight;
    el.value = saved;
    const len = saved.length;
    try {
      el.setSelectionRange(Math.min(ss, len), Math.min(se, len));
    } catch {
      /* ignore */
    }
    const lineDelta = Math.max(hTwoLines - hOneLine, 1);
    oneLineHeightRef.current = hOneLine;
    maxHeightCapRef.current = hOneLine + lineDelta * (MAX_LINES - 1);
  }, []);

  const syncTextareaHeight = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    if (maxHeightCapRef.current == null && !isComposingRef.current) {
      recalcMaxHeightCap();
    }
    const maxH = maxHeightCapRef.current ?? 120;

    if (el.value.length === 0 && !isComposingRef.current) {
      const single =
        oneLineHeightRef.current ??
        (() => {
          const saved = el.value;
          el.value = 'M';
          el.style.height = 'auto';
          const h = el.scrollHeight;
          el.value = saved;
          return h;
        })();
      el.style.height = `${single}px`;
      el.style.overflowY = 'hidden';
      return;
    }

    el.style.height = 'auto';
    const scrollH = el.scrollHeight;
    const nextH = Math.min(scrollH, maxH);
    el.style.height = `${nextH}px`;
    el.style.overflowY = scrollH > maxH ? 'auto' : 'hidden';
  }, [recalcMaxHeightCap]);

  useLayoutEffect(() => {
    syncTextareaHeight();
  }, [draft, canSend, syncTextareaHeight]);

  useLayoutEffect(() => {
    const el = textareaRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => {
      maxHeightCapRef.current = null;
      recalcMaxHeightCap();
      syncTextareaHeight();
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [recalcMaxHeightCap, syncTextareaHeight]);

  const handleSubmit = useCallback(() => {
    if (disabled) return;
    onSend(trimmed);
    setDraft('');
  }, [disabled, onSend, trimmed]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key !== 'Enter' || e.shiftKey) return;
      if (isComposingRef.current || e.nativeEvent.isComposing) return;
      e.preventDefault();
      handleSubmit();
    },
    [handleSubmit],
  );

  const emojiPickerLabel = t('chat_emoji_picker_open');

  return (
    <div
      className={cn('flex w-full shrink-0 flex-col gap-1 pt-1 @[18rem]:gap-1.5 @[18rem]:pt-1.5')}
    >
      <div className="flex min-w-0 items-stretch overflow-hidden rounded-md border border-slate-200 focus-within:ring-2 focus-within:ring-[#405820] focus-within:ring-offset-0">
        <div className="relative min-w-0 flex-1 self-start">
          <Textarea
            ref={textareaRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onCompositionStart={() => {
              isComposingRef.current = true;
            }}
            onCompositionEnd={() => {
              isComposingRef.current = false;
            }}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={!canSend}
            rows={1}
            className={cn(
              'min-h-0 w-full resize-none rounded-none border-0 border-r border-slate-200 py-1 pr-12 text-xs leading-normal shadow-none ring-0 ring-offset-0 placeholder:whitespace-nowrap focus-visible:ring-0 @[18rem]:py-1.5 @[18rem]:pr-14 @[18rem]:text-sm @[18rem]:leading-normal',
            )}
            aria-label={placeholder}
          />
          {/* Mask area so long placeholder never appears under the emoji icon */}
          <div
            aria-hidden
            className="pointer-events-none absolute top-0 right-0 h-full w-12 bg-[#e2e9c2] @[18rem]:w-14"
          />
          <Popover.Root open={emojiOpen} onOpenChange={handleEmojiOpenChange}>
            <Popover.Trigger asChild>
              <button
                type="button"
                disabled={!canSend}
                data-testid="chat-emoji-picker-trigger"
                className={cn(
                  'absolute top-1/2 right-1.5 z-10 flex size-7 -translate-y-1/2 items-center justify-center rounded-md text-slate-600 transition-colors',
                  'shadow-none ring-0 outline-none hover:bg-black/5 focus:shadow-none focus:ring-0 focus:ring-offset-0 focus:outline-none focus-visible:shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none',
                  'disabled:pointer-events-none disabled:opacity-40 @[18rem]:right-2 @[18rem]:size-8',
                )}
                aria-label={emojiPickerLabel}
                aria-expanded={emojiOpen}
                aria-haspopup="dialog"
              >
                <Smile className="size-4 @[18rem]:size-4.5" aria-hidden strokeWidth={1.75} />
              </button>
            </Popover.Trigger>
            <Popover.Portal>
              <Popover.Content
                side="top"
                align="end"
                sideOffset={8}
                collisionPadding={8}
                className={cn(
                  'z-200 w-[min(calc(100vw-1rem),20rem)] max-w-[min(calc(100vw-1rem),20rem)] rounded-lg border-0 bg-transparent p-0 shadow-none',
                  'focus:outline-none',
                )}
              >
                <EmojiPicker
                  locale={toFrimousseLocale(locale)}
                  className="h-[min(42vh,20rem)] max-h-[20rem]"
                  onEmojiSelect={({ emoji }) => applyEmoji(emoji)}
                >
                  {recents.length > 0 ? (
                    <div
                      className="shrink-0 border-b border-[#405820]/15 bg-[#e8edc8]/90 px-2 py-1.5 dark:border-white/10 dark:bg-neutral-800/50"
                      data-testid="chat-emoji-recents"
                    >
                      <div className="mb-1 text-[0.65rem] font-medium tracking-wide text-slate-600 uppercase dark:text-neutral-400">
                        {t('chat_emoji_recent')}
                      </div>
                      <div
                        className="flex max-w-full [scrollbar-width:none] gap-0.5 overflow-x-auto pb-0.5 [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
                        role="list"
                      >
                        {recents.map((e) => (
                          <button
                            key={e}
                            type="button"
                            data-testid="chat-recent-emoji"
                            data-emoji={e}
                            className="flex size-8 shrink-0 items-center justify-center rounded-md text-lg hover:bg-[#9ca965]/40 dark:hover:bg-neutral-700"
                            onClick={() => applyEmoji(e)}
                            aria-label={`${t('chat_emoji_recent')}: ${e}`}
                          >
                            {e}
                          </button>
                        ))}
                      </div>
                    </div>
                  ) : null}
                  <EmojiPickerSearch placeholder={t('chat_emoji_search_placeholder')} />
                  <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
                    <EmojiPickerContent
                      className="min-h-[11rem] flex-1"
                      emptyLabel={t('chat_emoji_empty')}
                    />
                    <EmojiPickerFooter
                      selectHint={t('chat_emoji_select_hint')}
                      skinToneAriaLabel={t('chat_emoji_skin_tone_aria')}
                    />
                  </div>
                </EmojiPicker>
              </Popover.Content>
            </Popover.Portal>
          </Popover.Root>
        </div>
        <Button
          type="button"
          onClick={handleSubmit}
          disabled={disabled}
          className={cn(
            'h-auto min-h-0 w-9 shrink-0 self-stretch rounded-none border-0 bg-[#405820] p-0 text-white hover:bg-[#405820]/90 disabled:opacity-50 @[18rem]:w-10',
          )}
          size="sm"
          aria-label={sendLabel}
        >
          <PaperPlaneIcon className="h-4 w-4 @[18rem]:h-4.5 @[18rem]:w-4.5" aria-hidden />
        </Button>
      </div>
    </div>
  );
};

export default ChatComposer;
